/**
 * Feature Flag Service
 * Manages feature visibility based on hospital and department configuration
 */

export type FeatureName =
  | 'AI_CHATBOT'
  // Add more features here as needed
  ;

interface FeatureConfig {
  // List of enabled configurations. Each entry can specify hospital and optionally department
  enabled: Array<{
    hospital: string; // Can be Hebrew name, English name, or MongoDB ObjectId
    department?: string; // Optional: Can be Hebrew name, English name, or MongoDB ObjectId
  }>;
}

// The protocol AI chat is frozen: hidden from everyone, super admins included,
// and from the ?enable-ai-chatbox link. To restore, follow "Frozen" in
// docs/protocols/Protocol-Wiki.md.
export const AI_CHAT_ENABLED = false;

// Feature flag configuration map
const FEATURE_FLAGS: Record<FeatureName, FeatureConfig> = {
  AI_CHATBOT: {
    enabled: [
      // {
      //   hospital: 'rambam',
      //   department: 'intensive care unit'
      // }
    ]
  }
};

interface Hospital {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  departments: Department[];
}

interface Department {
  _id: string;
  nameHebrew: string;
  nameEnglish: string;
  hospital: string;
}

class FeatureFlagService {
  /**
   * Check if a feature is enabled for any of the user's hospitals/departments
   */
  isFeatureEnabled(
    featureName: FeatureName,
    userHospitals: Hospital[],
    isSuperAdmin: boolean = false
  ): boolean {
    // Super admins have access to all features
    if (isSuperAdmin) {
      return true;
    }

    const featureConfig = FEATURE_FLAGS[featureName];
    if (!featureConfig) {
      return false;
    }

    // Check if any of the user's hospitals/departments match the enabled configurations
    return featureConfig.enabled.some(config => {
      // Find matching hospital
      const matchingHospital = userHospitals.find(hospital =>
        this.matchesIdentifier(hospital, config.hospital)
      );

      if (!matchingHospital) {
        return false;
      }

      // If no department is specified in config, hospital match is enough
      if (!config.department) {
        return true;
      }

      // Check if any of the hospital's departments match
      return matchingHospital.departments.some(dept =>
        this.matchesIdentifier(dept, config.department!)
      );
    });
  }

  /**
   * Check if a specific hospital has access to a feature
   */
  isFeatureEnabledForHospital(
    featureName: FeatureName,
    hospital: Hospital | string,
    isSuperAdmin: boolean = false
  ): boolean {
    if (isSuperAdmin) {
      return true;
    }

    const featureConfig = FEATURE_FLAGS[featureName];
    if (!featureConfig) {
      return false;
    }

    const hospitalObj = typeof hospital === 'string'
      ? { _id: hospital, nameHebrew: hospital, nameEnglish: hospital, departments: [] }
      : hospital;

    return featureConfig.enabled.some(config => {
      const matches = this.matchesIdentifier(hospitalObj, config.hospital);
      // For hospital-only check, ignore department restrictions
      return matches && !config.department;
    });
  }

  /**
   * Check if a specific department has access to a feature
   */
  isFeatureEnabledForDepartment(
    featureName: FeatureName,
    hospital: Hospital | string,
    department: Department | string,
    isSuperAdmin: boolean = false
  ): boolean {
    if (isSuperAdmin) {
      return true;
    }

    const featureConfig = FEATURE_FLAGS[featureName];
    if (!featureConfig) {
      return false;
    }

    const hospitalObj = typeof hospital === 'string'
      ? { _id: hospital, nameHebrew: hospital, nameEnglish: hospital, departments: [] }
      : hospital;

    const deptObj = typeof department === 'string'
      ? { _id: department, nameHebrew: department, nameEnglish: department, hospital: '' }
      : department;

    return featureConfig.enabled.some(config => {
      const hospitalMatches = this.matchesIdentifier(hospitalObj, config.hospital);
      if (!hospitalMatches) {
        return false;
      }

      // If config doesn't specify department, hospital match is enough
      if (!config.department) {
        return true;
      }

      return this.matchesIdentifier(deptObj, config.department);
    });
  }

  /**
   * Helper method to match an entity (hospital/department) against an identifier
   * Identifier can be: MongoDB ObjectId, Hebrew name, or English name (case-insensitive)
   */
  private matchesIdentifier(
    entity: { _id: string; nameHebrew: string; nameEnglish: string },
    identifier: string
  ): boolean {
    const normalizedIdentifier = identifier.toLowerCase().trim();

    return (
      entity._id === identifier ||
      entity.nameHebrew.toLowerCase().trim() === normalizedIdentifier ||
      entity.nameEnglish.toLowerCase().trim() === normalizedIdentifier
    );
  }

  /**
   * Get all features enabled for a user
   */
  getEnabledFeatures(
    userHospitals: Hospital[],
    isSuperAdmin: boolean = false
  ): FeatureName[] {
    if (isSuperAdmin) {
      return Object.keys(FEATURE_FLAGS) as FeatureName[];
    }

    return (Object.keys(FEATURE_FLAGS) as FeatureName[]).filter(featureName =>
      this.isFeatureEnabled(featureName, userHospitals, isSuperAdmin)
    );
  }
}

// Export singleton instance
export const featureFlagService = new FeatureFlagService();
